import { UIEvent, useEffect, useRef } from "react";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { useAiChats } from "lib/hooks/api/ai_chat/useAiChats";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useAiContext } from "lib/providers/AiContextProvider";
import { EventNames, useTracking } from "lib/tracking";

type AiChatHistoryVariant = "sidebar" | "inline";

type ChatsHistoryProps = {
  variant?: AiChatHistoryVariant;
  onBackClick?: () => void; // for inline
  onNewChatClick?: () => void; // for sidebar
  onSelectConversation: (conversationId: string) => void;
  currentConversationId?: string;
  containerPadding?: string;
};

export default function AiChatHistory({
  variant = "sidebar",
  onBackClick,
  onNewChatClick,
  onSelectConversation,
  currentConversationId,
  containerPadding,
}: ChatsHistoryProps) {
  const { logEvent } = useTracking();
  const { resourceId, resourceName } = useAiContext();

  const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } = useAiChats();
  const chats = data?.pages.flatMap((page) => page.aiChats);

  const listRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const threshold = 48; // Pixels from the bottom before we trigger

    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= threshold && hasNextPage && !isFetchingNextPage && !isLoading) {
      void fetchNextPage();
    }
  };

  // Auto-fetch next page if the content doesn't fill the container
  useEffect(() => {
    const element = listRef.current;
    if (!element) return;

    const canScroll = element.scrollHeight > element.clientHeight + 1;
    if (hasNextPage && !isFetchingNextPage && !isLoading && !canScroll) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, chats?.length, fetchNextPage]);

  return (
    <ChatsHistoryContainer variant={variant} containerPadding={containerPadding}>
      {variant === "sidebar" && (
        <NewChatButtonContainer>
          <TransparentButton buttonType="tertiary" onClick={onNewChatClick} fullWidth>
            <Flex justifyContent="space-between" alignItems="center" width="100%">
              New Chat
              <UIcon icon="edit02" />
            </Flex>
          </TransparentButton>
        </NewChatButtonContainer>
      )}

      {variant === "inline" && (
        <InlineHeader alignItems="flex-start" column gap={16}>
          <Button buttonType="secondary" icon="arrowLeft" onClick={onBackClick} />
          <Text h3>Chat history</Text>
        </InlineHeader>
      )}

      <OldChatsContainer ref={listRef} onScroll={handleScroll}>
        {isLoading && (
          <>
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
          </>
        )}

        {chats &&
          chats.length > 0 &&
          chats.map((conversation) => {
            const { conversationId, title } = conversation;

            return (
              <TransparentButton
                key={conversationId}
                buttonType="tertiary"
                onClick={() => {
                  logEvent(EventNames.chatSelected, {
                    "Chat name": title,
                    "Chat Id": conversationId,
                    "Resource Id": resourceId,
                    "Resource name": resourceName,
                    "Context source": "Ask AI",
                  });
                  onSelectConversation(conversationId);
                }}
              >
                <ClassNames>
                  {({ css, theme }) => (
                    <EllipsisTooltipText
                      fullText={title || "Untitled chat"}
                      containerClassname={css({
                        textWrap: "nowrap",
                        width: variant === "sidebar" ? 182 : "100%",
                        color:
                          variant === "sidebar" && currentConversationId === conversationId
                            ? theme.colors.sysPrimaryDefault
                            : theme.colors.sysTextBlack,
                      })}
                    />
                  )}
                </ClassNames>
              </TransparentButton>
            );
          })}

        {isFetchingNextPage && (
          <>
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
          </>
        )}
      </OldChatsContainer>
    </ChatsHistoryContainer>
  );
}

const ChatsHistoryContainer = styled(Flex)<{
  variant: AiChatHistoryVariant;
  containerPadding?: string;
}>(({ variant, containerPadding = "0 16px 0 0" }) => ({
  flexDirection: "column",
  flexShrink: 0,
  padding: containerPadding,
  width: variant === "sidebar" ? 240 : "100%",
  height: "100%",
}));

const NewChatButtonContainer = styled(Flex)(({ theme }) => ({
  borderBottom: "1px solid " + theme.colors.sysBorderDecorative,
  paddingBottom: 8,
}));

const OldChatsContainer = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  overflowY: "auto",
  marginTop: 16,
  maxHeight: "100%",
});

const TransparentButton = styled(Button)({
  backgroundColor: "transparent",
});

const InlineHeader = styled(Flex)({
  padding: "0 8px",
});
